import axios from 'axios'
import { api } from './instance'
import type { LogEvent, PagedResult, EventQueryParams, GroupedEventQueryParams, GroupedEventsResponse, SharedEvent } from '../types'

export const eventsApi = {
  getEvents: async (params: EventQueryParams, signal?: AbortSignal): Promise<PagedResult<LogEvent>> => {
    const queryParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return

      if (key === 'levels' && Array.isArray(value)) {
        value.forEach(level => queryParams.append('levels', level))
      } else if (key === 'annotationSeverities' && Array.isArray(value)) {
        value.forEach(sev => queryParams.append('annotationSeverities', sev))
      } else {
        queryParams.append(key, String(value))
      }
    })

    const { data } = await api.get(`/events?${queryParams.toString()}`, { signal })
    return data
  },

  getEventById: async (id: string): Promise<LogEvent> => {
    const { data } = await api.get(`/events/${id}`)
    return data
  },

  getEventsByTraceId: async (traceId: string): Promise<LogEvent[]> => {
    const { data } = await api.get(`/events/trace/${traceId}`)
    return data
  },

  getApplications: async (): Promise<string[]> => {
    const { data } = await api.get('/events/applications')
    return data
  },

  getGroupedEvents: async (params: GroupedEventQueryParams): Promise<GroupedEventsResponse> => {
    const queryParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return

      if (key === 'levels' && Array.isArray(value)) {
        value.forEach(level => queryParams.append('levels', level))
      } else {
        queryParams.append(key, String(value))
      }
    })

    const { data } = await api.get(`/events/grouped?${queryParams.toString()}`)
    return data
  },

  generateShareToken: async (eventId: string): Promise<{ token: string; url: string; eventId: string }> => {
    const { data } = await api.post(`/events/${eventId}/share`)
    return data
  },

  getEventContext: async (eventId: string, before = 50, after = 50): Promise<{
    eventsBefore: LogEvent[]
    targetEvent: LogEvent | null
    eventsAfter: LogEvent[]
    found: boolean
    totalEvents: number
  }> => {
    const { data } = await api.get(`/events/${eventId}/context`, {
      params: { before, after }
    })
    return data
  },

  purgePreview: async (params: {
    application?: string
    levels?: string[]
    dateFrom?: string
    dateTo?: string
    filterExpression?: string
  }): Promise<{ count: number }> => {
    const { data } = await api.post('/events/purge/preview', params)
    return data
  },

  getHistogram: async (params: { timeRange?: string; application?: string; filter?: string }): Promise<{
    buckets: Array<Record<string, unknown>>
    timeRange: string
    granularity: string
  }> => {
    const queryParams = new URLSearchParams()
    if (params.timeRange) queryParams.append('timeRange', params.timeRange)
    if (params.application) queryParams.append('application', params.application)
    if (params.filter) queryParams.append('filter', params.filter)
    const { data } = await api.get(`/events/histogram?${queryParams.toString()}`)
    return data
  },

  purgeExecute: async (params: {
    application?: string
    levels?: string[]
    dateFrom?: string
    dateTo?: string
    filterExpression?: string
    password: string
  }): Promise<{ deletedCount: number; success: boolean }> => {
    const { data } = await api.post('/events/purge/execute', params)
    return data
  },
}

// Public API (no authentication required)
export const publicApi = {
  getSharedEvent: async (token: string): Promise<SharedEvent> => {
    const { data } = await axios.get(`/api/public/events/${token}`)
    return data
  },
}
